import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "../../../../Axios";
import { validateAndReadFile } from "../../../../Shared_utilities/fileReadingUtils";
import { Urls } from "../../../../Urls";
import useRPTForecast from "../../hooks/useRPTForecast";
import ForecastResult from "../ForecastResult/ForecastResult";
import TruckDimensionReview from "../TruckSelector/TruckDimensionReview";
import FileReviewScreen from "./FileReviewScreen";
import FileUploadSection from "./FileUploadSection";
import ManualInputForm from "./ManualInputForm";
import styles from "./RptBody.module.css";
import { parseUserDate } from "../../../../Shared_utilities/dateUtils";
import FileUploadResultScreen from "./FileUploadResultsScreen";
import CenteredLoader from "./CenteredLaoder";
import { useMixPanelForTracking } from "../../../../Shared_utilities/trackmixpanel";
import { useEffect } from "react";

const baseTruckTypeObject = {
  raw_truck_name: "",
  cleaned_code: "",
  user_dimensions: {
    capacity_mt: "",
    length_ft: "",
    axle_type: "",
    body_type: "",
  },
  cleaned_dimensions: {
    dimensions: {
      capacity_mt: "",
      length_ft: "",
      axle_type: "",
      body_type: "",
    },
  },
  status: null,
};

export default function RptBody() {
  const [origin, setOrigin] = useState({});
  const [destination, setDestination] = useState({});
  const [truckType, setTruckType] = useState(baseTruckTypeObject);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [upload, setUpload] = useState({
    file: null,
    loading: false,
    error: null,
    blobUrl: null,
    fileName: null,
  });
  const {TrackMixPanel} = useMixPanelForTracking()

  const [cleanedLanes, setCleanedLanes] = useState([]);
  const [viewType, setViewType] = useState("input"); // "input", "review", "result"
  const [validationError, setValidationError] = useState({
    origin: false,
    destination: false,
    truckType: false,
    date: false,
  });

  const fileInputRef = useRef(null);
  const [startDate, endDate] = dateRange;
  const { forecastResult, isLoading, handleForecast, getHeaderMapping } =
    useRPTForecast();
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  
  useEffect(()=>{
  TrackMixPanel({
    "ModuleName":"lorri ai",
    "ScreenName":"lorri ai - rpt",
    "Definition":"the user has opened rpt."
  })
  },[])

  const inputMode = useMemo(() => {
    const isTruckFilled = truckType.cleaned_code?.trim().length > 0;
    const isLocationFilled =
      origin?.value?.length > 0 || destination?.value?.length > 0;
    // if (!isTruckFilled && !isLocationFilled && !upload.file) return null;
    if (!isTruckFilled && !isLocationFilled && !upload.file) return "manual";
    if (!isTruckFilled && !isLocationFilled && upload.file) return "upload";
    return "manual";
  }, [origin, destination, truckType, upload.file]);

  async function parseAndValidateFile(file) {
    const { data, headers, error } = await validateAndReadFile(file, ["excel"]);
    if (error) throw new Error("File parsing failed");

    const sampleRows = data
      .slice(0, 2)
      .map((row) => headers.map((h) => row[h]));

    const { mapping, error: mappingError } = await getHeaderMapping(
      headers,
      sampleRows
    );

    if (mappingError || !mapping) {
      setUpload((prev) => {
        return { ...prev, error: mappingError };
      });
      return { data, mapping: null };
    }

    return { data, mapping };
  }

  function transformLanesFromRaw(data, mapping) {
    return data.map((row) => ({
      origin: row[mapping.origin_name],
      destination: row[mapping.destination_name],
      truck_type: row[mapping.truck_type],
      date: row[mapping.date_column],
    }));
  }

  async function fetchCleanedTrucks(lanes) {
    const uniqueTrucks = [
      ...new Set(lanes.map((l) => l.truck_type?.trim()).filter(Boolean)),
    ];
    const bulkCleanTruckUrl = `${Urls.rpt_base_url}/bulk_clean_truck`;

    const { data: cleaned = [] } = await axios.post(bulkCleanTruckUrl, {
      raw_truck_names: uniqueTrucks,
    });

    const truckMap = {};
    cleaned.forEach((t) => {
      truckMap[t.raw_truck_name.trim()] = {
        raw_truck_name: t.raw_truck_name || "",
        cleaned_code: t.cleaned_code || "",
        cleaned_dimensions: {
          dimensions: { ...t.dimensions },
        },
        user_dimensions: { ...t.dimensions },
        status: "inferred",
      };
    });

    return truckMap;
  }

  async function fetchLocationSuggestions(lanes) {
    const allLocs = [
      ...new Set(
        lanes.flatMap((l) => [l.origin?.trim(), l.destination?.trim()])
      ),
    ].filter(Boolean);
    const locationList = allLocs.map((loc, i) => ({
      raw_key: `loc_${i}`,
      raw_location: loc,
      raw_district: "",
      raw_state: "",
      raw_location_code: "",
    }));

    const { data = [] } = await axios.post(
      `${Urls.Autocomplete.Get.getlist}?bulk_suggestions=true`,
      {
        location_list: locationList,
      }
    );

    const locMap = {};
    data.forEach((item) => {
      locMap[item.raw_location.trim()] = item.value?.[0] || null;
    });
    return locMap;
  }

  function enrichLanes(lanes, truckMap, locationMap, mapping) {
    return lanes.map((lane) => {
      const userDate = parseUserDate(lane.date);

      return {
        ...lane,
        origin_details: locationMap[lane.origin?.trim()] || null,
        destination_details: locationMap[lane.destination?.trim()] || null,
        truck_details: truckMap[lane.truck_type?.trim()] || baseTruckTypeObject,
        date: userDate,
      };
    });
  }

  const handleGenerate = async () => {
    if (inputMode === "manual") {
      const errors = {
        origin: !origin?.value,
        destination: !destination?.value,
        truckType: !truckType?.cleaned_code,
        date: !startDate || isNaN(startDate.getTime()),
      };
      setValidationError(errors);

      const hasErrors = Object.values(errors).some(Boolean);
      if (hasErrors) {
        console.error("Please fill all required fields.");
        return;
      }

      await handleForecast({ origin, destination, truckType, startDate });
      setViewType("result");
      return;
    }

    try {
      setIsFileProcessing(true);

      const { data, mapping } = await parseAndValidateFile(upload.file);
      const transformedLanes = transformLanesFromRaw(data, mapping);

      const [truckMap, locationMap] = await Promise.all([
        fetchCleanedTrucks(transformedLanes),
        fetchLocationSuggestions(transformedLanes),
      ]);

      const enrichedLanes = enrichLanes(
        transformedLanes,
        truckMap,
        locationMap,
        mapping
      );

      setCleanedLanes(enrichedLanes);
      setViewType("review");
    } catch (err) {
      console.error("Upload flow failed", err);
      toast.error(err.message || "Could not process file. Please try again.");
    } finally {
      setIsFileProcessing(false);
    }
  };

  const handleReset = () => {
    setOrigin({});
    setDestination({});
    setTruckType(baseTruckTypeObject);
    setDateRange([new Date(), new Date()]);
    setUpload({
      file: null,
      loading: false,
      error: null,
      blobUrl: null,
      fileName: null,
    });
    setCleanedLanes([]);
    setViewType("input");
  };

  if (viewType === "review") {
    return (
      <FileReviewScreen
        lanes={cleanedLanes}
        setLanes={setCleanedLanes}
        onSubmit={() => {
          TrackMixPanel({
            "ModuleName":"lorri ai",
            "ScreenName":"lorri ai - rpt",
            "Definition":"user has clicked on the generate forecast button.",
            "Data":{
              "Lane":cleanedLanes
            }
          })
          setViewType("result");
        }}
        onBack={() => setViewType("input")}
      />
    );
  }

  if (viewType === "result" && inputMode === "upload") {
    return <FileUploadResultScreen lanes={cleanedLanes} onBack={handleReset} />;
  }

  if (viewType === "result" && inputMode === "manual" && forecastResult) {
    return (
      <ForecastResult
        predictedPrice={forecastResult.predicted_base_price}
        origin={origin}
        destination={destination}
        truckType={truckType}
        startDate={startDate}
        onBack={handleReset}
      />
    );
  }

  return (
    <div className={styles.rptBodyContainer}>
      {isFileProcessing && (
        <CenteredLoader message="Processing uploaded file..." />
      )}

      <div className={styles.rptContent}>
        <h3 className={styles.title}>Forecast freight Rate for Lanes</h3>
        <p className={styles.subtitle}>
          Enter the origin, destination, truck type, and date to predict freight
          rates. This tool provides indicative price trends based on historical
          and market intelligence data.
        </p>

        <ManualInputForm
          origin={origin}
          setOrigin={setOrigin}
          destination={destination}
          setDestination={setDestination}
          truckType={truckType}
          setTruckType={setTruckType}
          dateRange={dateRange}
          setDateRange={setDateRange}
          inputMode={inputMode}
          validationError={validationError}
        />

        <TruckDimensionReview
          show={
            truckType.status === "inferred" || truckType.status === "edited"
          }
          dimensions={truckType.user_dimensions}
          onChange={(field, value) =>
            setTruckType((prev) => ({
              ...prev,
              user_dimensions: {
                ...prev.user_dimensions,
                [field]: value,
              },
              status: prev.status === "inferred" ? "edited" : prev.status,
            }))
          }
        />

        <div className={styles.separator}>OR</div>

        <FileUploadSection
          uploadState={upload}
          setUploadState={setUpload}
          fileInputRef={fileInputRef}
        />

        <ul className={styles.hints}>
          <li>
            This forecast is generated using historical data, diesel trends, and
            inferred demand.
          </li>
          <li>
            Actual market rates may vary due to local, seasonal, and real-time
            factors.
          </li>
          <li>
            Lorri Intelligence assumes no liability for decisions made solely
            based on predicted rates.
          </li>
        </ul>
      </div>

      <div className={styles.buttonRow}>
        <button className={styles.cancelBtn} onClick={handleReset}>
          Cancel
        </button>
        <button
          className={styles.submitBtn}
          onClick={handleGenerate}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Generate Forecast"}
        </button>
      </div>
    </div>
  );
}
